// CORE IMPORTS
import { Box, Typography, styled, Link } from "@mui/material";

interface TradeHelpConfig {
  title: string;
  description?: string;
  links?: Array<{
    text: string;
    url: string;
  }>;
}

interface NoOrdersProps {
  modalPage?: string;
  tradeType: "swap" | "target" | "scheduled";
  showHelp?: boolean;
}

const TRADE_HELP_CONFIG: Record<string, TradeHelpConfig> = {
  swap: {
    title: "Not sure how to place a trade?",
    description: "Speak to your broker or view our quick link below.",
    links: [
      {
        text: "How to Trade with Caleb & Brown",
        url: "https://support.calebandbrown.com/en/articles/6575819-how-do-i-trade-with-caleb-brown",
      },
    ],
  },
  target: {
    title: "Not sure what target is or how to place one?",
    description: "Speak to your broker here or view our quick links below.",
    links: [
      {
        text: "How to place a Target Order",
        url: "https://support.calebandbrown.com/en/articles/11182116-how-to-place-a-target-order-limit-order",
      },
    ],
  },
  scheduled: {
    title: "Not sure what Scheduled order is or how to place one?",
    description: "Speak to your broker here or view our quick links below",
    links: [
      { text: "What is a Scheduled Order?", url: "https://support.calebandbrown.com/en/articles/9303915-what-is-dca" },
      { text: "How to set up a Scheduled Order", url: "https://support.calebandbrown.com/en/articles/9303919-how-to-set-up-a-scheduled-order" },
    ],
  },
};

const Container = styled(Box)({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
  justifyContent: "space-between",
});

const MainContent = styled(Box)({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
});

const StatusText = styled(Typography)({
  color: "#5C6469",
  fontSize: "16px",
  lineHeight: "24px",
  textAlign: "center",
});

const HelpSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  background: "#F8F9F9",
  width: "100%",
  padding: "24px 32px",
  borderRadius: "10px",
});

const HelpTitle = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "16px",
  textAlign: "center",
  color: "#000000",
});

const HelpDescription = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "20px",
  textAlign: "center",
  color: "#5C6469",
  maxWidth: "360px",
});

const HelpLink = styled(Link)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "17px",
  color: "#FC4C4C",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.7,
    textDecoration: "underline",
  },
});

const LinksContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
});

export const NoOrders = ({ modalPage, tradeType, showHelp = true }: NoOrdersProps) => {
  const getStatusMessage = () => {
    if (!modalPage) return "";

    if (modalPage === "inProgress") {
      return `You currently have no ${tradeType} orders in progress.`;
    }
    return `You currently have no completed or cancelled ${tradeType} orders.`;
  };

  const helpConfig = TRADE_HELP_CONFIG[tradeType];

  return (
    <Container>
      <MainContent>{modalPage && <StatusText>{getStatusMessage()}</StatusText>}</MainContent>

      {showHelp && helpConfig && (
        <HelpSection>
          <Box>
            <HelpTitle>{helpConfig.title}</HelpTitle>
            {helpConfig.description && <HelpDescription>{helpConfig.description}</HelpDescription>}
          </Box>
          {helpConfig.links && helpConfig.links.length > 0 && (
            <LinksContainer>
              {helpConfig.links.map((link, index) => (
                <HelpLink key={index} href={link.url} target="_blank" rel="noopener noreferrer">
                  {link.text}
                </HelpLink>
              ))}
            </LinksContainer>
          )}
        </HelpSection>
      )}
    </Container>
  );
};
